import {
  ApiError,
  type CreatedWikiShareLink,
  queryKeys,
  type WikiAccessCandidates,
  type WikiPageAccess,
} from '@mevaser/shared'
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  Input,
  Switch,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Lock, Plus, X } from 'lucide-react'
import { useEffect, useState } from 'react'
import { api } from '../lib/api.js'

const STATUS_TONE = { active: 'success', expired: 'warning', revoked: 'danger' } as const

/**
 * The "Access" card on the page editor (t0932) — who can open this page
 * when it's restricted, and every share link ever issued for it. Rendered
 * only for `wiki:manage`, same gate as the rest of the editor's destructive
 * actions — see `WikiPageDetailPage` in `wiki-page.tsx`.
 */
export function PageAccessCard({ pageId }: { pageId: string }) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [picking, setPicking] = useState(false)
  const [creatingLink, setCreatingLink] = useState(false)

  const { data: access } = useQuery({
    queryKey: queryKeys.wiki.pageAccess(pageId),
    queryFn: () => api.get<WikiPageAccess>(`/wiki/pages/${pageId}/access`),
  })

  const setAccess = useMutation({
    mutationFn: (patch: { restricted?: boolean; userIds?: string[]; roleIds?: string[] }) =>
      api.put<WikiPageAccess>(`/wiki/pages/${pageId}/access`, {
        restricted: patch.restricted ?? access?.restricted ?? false,
        userIds: patch.userIds ?? access?.users.map((u) => u.userId) ?? [],
        roleIds: patch.roleIds ?? access?.roles.map((r) => r.roleId) ?? [],
      }),
    onSuccess: async (updated) => {
      queryClient.setQueryData(queryKeys.wiki.pageAccess(pageId), updated)
      await queryClient.invalidateQueries({ queryKey: queryKeys.wiki.all() })
    },
    onError: (error: Error) =>
      toast.error(
        'Could not update access',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const revokeLink = useMutation({
    mutationFn: (linkId: string) => api.post(`/wiki/links/${linkId}/revoke`, {}),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.wiki.pageAccess(pageId) })
      toast.success('Link revoked')
    },
    onError: (error: Error) =>
      toast.error(
        'Could not revoke the link',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  if (!access) return null

  return (
    <Card>
      <CardHeader>
        <CardTitle>Access</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-start gap-3">
          <Switch
            id="page-restricted"
            checked={access.restricted}
            onCheckedChange={(checked) => setAccess.mutate({ restricted: checked })}
          />
          <label className="text-sm" htmlFor="page-restricted">
            Restrict this page
            <span className="block text-xs text-foreground-muted">
              {access.restricted
                ? 'Only the people and roles granted below can open it — other staff with wiki access cannot.'
                : 'Visible to everyone with wiki access, the same as any other page in its section.'}
            </span>
          </label>
        </div>

        {access.restricted ? (
          <div className="space-y-2 border-t border-border pt-3">
            <div className="flex items-center justify-between">
              <p className="text-2xs font-medium uppercase tracking-wide text-foreground-muted">
                Granted
              </p>
              <Button variant="ghost" size="sm" onClick={() => setPicking(true)}>
                <Plus className="size-3.5" /> Add
              </Button>
            </div>
            {access.users.length === 0 && access.roles.length === 0 ? (
              <p className="text-xs text-foreground-muted">
                Nobody is granted yet — only staff holding wiki:manage can open this page.
              </p>
            ) : (
              <ul className="space-y-1">
                {access.roles.map((role) => (
                  <li key={role.roleId} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-1.5">
                      <Badge tone="info">Role</Badge> {role.name}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove ${role.name}`}
                      onClick={() =>
                        setAccess.mutate({
                          roleIds: access.roles
                            .filter((r) => r.roleId !== role.roleId)
                            .map((r) => r.roleId),
                        })
                      }
                    >
                      <X className="size-3.5" />
                    </Button>
                  </li>
                ))}
                {access.users.map((user) => (
                  <li key={user.userId} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-1.5">
                      <Badge tone={user.roleKey === 'guest' ? 'neutral' : 'primary'}>
                        {user.roleKey === 'guest' ? 'Guest' : 'Staff'}
                      </Badge>
                      {user.name}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Remove ${user.name}`}
                      onClick={() =>
                        setAccess.mutate({
                          userIds: access.users
                            .filter((u) => u.userId !== user.userId)
                            .map((u) => u.userId),
                        })
                      }
                    >
                      <X className="size-3.5" />
                    </Button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ) : null}

        <div className="space-y-2 border-t border-border pt-3">
          <div className="flex items-center justify-between">
            <p className="text-2xs font-medium uppercase tracking-wide text-foreground-muted">
              Share links
            </p>
            <Button variant="ghost" size="sm" onClick={() => setCreatingLink(true)}>
              <Plus className="size-3.5" /> New link
            </Button>
          </div>
          {access.links.length === 0 ? (
            <p className="text-xs text-foreground-muted">No links created for this page.</p>
          ) : (
            <ul className="space-y-1.5">
              {access.links.map((link) => (
                <li key={link.id} className="flex items-center justify-between gap-2 text-sm">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                      {link.hasPassword ? (
                        <Lock className="size-3 shrink-0 text-foreground-muted" aria-hidden="true" />
                      ) : null}
                      <span className="truncate">{link.label ?? 'Untitled link'}</span>
                      <Badge tone={STATUS_TONE[link.status]}>{link.status}</Badge>
                    </div>
                    <p className="text-2xs text-foreground-muted">
                      {link.expiresAt
                        ? `Expires ${new Date(link.expiresAt).toLocaleString()}`
                        : 'No expiration'}
                      {link.lastAccessedAt
                        ? ` · Last used ${new Date(link.lastAccessedAt).toLocaleDateString()}`
                        : ''}
                    </p>
                  </div>
                  {link.status === 'active' ? (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => revokeLink.mutate(link.id)}
                      disabled={revokeLink.isPending}
                    >
                      Revoke
                    </Button>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      </CardContent>

      <AccessPickerDialog
        open={picking}
        onOpenChange={setPicking}
        access={access}
        onSave={(userIds, roleIds) => {
          setAccess.mutate({ userIds, roleIds })
          setPicking(false)
        }}
      />
      <CreateLinkDialog open={creatingLink} onOpenChange={setCreatingLink} pageId={pageId} />
    </Card>
  )
}

function AccessPickerDialog({
  open,
  onOpenChange,
  access,
  onSave,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  access: WikiPageAccess
  onSave: (userIds: string[], roleIds: string[]) => void
}) {
  const { data: candidates } = useQuery({
    queryKey: queryKeys.wiki.accessCandidates(),
    queryFn: () => api.get<WikiAccessCandidates>('/wiki/access-candidates'),
    enabled: open,
  })

  const [userIds, setUserIds] = useState<Set<string>>(new Set())
  const [roleIds, setRoleIds] = useState<Set<string>>(new Set())

  useEffect(() => {
    if (!open) return
    setUserIds(new Set(access.users.map((u) => u.userId)))
    setRoleIds(new Set(access.roles.map((r) => r.roleId)))
  }, [open, access])

  const toggle = (set: Set<string>, setter: (next: Set<string>) => void, id: string) => {
    const next = new Set(set)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setter(next)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Grant access</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <Field label="Roles" hint="Every staff member holding one of these roles.">
            {() => (
              <ul className="max-h-32 space-y-1 overflow-y-auto scrollbar-thin rounded-md border border-border p-2">
                {(candidates?.roles ?? []).map((role) => (
                  <li key={role.id}>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="size-3.5 accent-[var(--primary)]"
                        checked={roleIds.has(role.id)}
                        onChange={() => toggle(roleIds, setRoleIds, role.id)}
                      />
                      {role.name}
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </Field>
          <Field label="People" hint="Specific staff members or invited guests.">
            {() => (
              <ul className="max-h-48 space-y-1 overflow-y-auto scrollbar-thin rounded-md border border-border p-2">
                {(candidates?.users ?? []).map((user) => (
                  <li key={user.id}>
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="size-3.5 accent-[var(--primary)]"
                        checked={userIds.has(user.id)}
                        onChange={() => toggle(userIds, setUserIds, user.id)}
                      />
                      {user.name}
                      {user.roleKey === 'guest' ? <Badge tone="neutral">Guest</Badge> : null}
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => onSave([...userIds], [...roleIds])}>
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function CreateLinkDialog({
  open,
  onOpenChange,
  pageId,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  pageId: string
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [label, setLabel] = useState('')
  const [password, setPassword] = useState('')
  const [expiresAt, setExpiresAt] = useState('')
  const [created, setCreated] = useState<CreatedWikiShareLink | null>(null)

  const reset = () => {
    setLabel('')
    setPassword('')
    setExpiresAt('')
    setCreated(null)
  }

  const create = useMutation({
    mutationFn: () =>
      api.post<CreatedWikiShareLink>(`/wiki/pages/${pageId}/links`, {
        label: label.trim() || undefined,
        password: password || undefined,
        expiresAt: expiresAt ? new Date(expiresAt).toISOString() : undefined,
      }),
    onSuccess: async (result) => {
      setCreated(result)
      await queryClient.invalidateQueries({ queryKey: queryKeys.wiki.pageAccess(pageId) })
    },
    onError: (error: Error) =>
      toast.error(
        'Could not create the link',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next)
        if (!next) reset()
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{created ? 'Link created' : 'New share link'}</DialogTitle>
        </DialogHeader>
        {created ? (
          <>
            <DialogBody className="space-y-3">
              <p className="text-sm text-foreground-muted">
                Copy it now — it won't be shown again. Anyone with this link can view the page,
                even while it's unpublished.
              </p>
              <div className="flex items-center gap-2">
                <Input readOnly value={created.url} className="font-identifier" />
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    void navigator.clipboard.writeText(created.url)
                    toast.success('Copied')
                  }}
                >
                  Copy
                </Button>
              </div>
            </DialogBody>
            <DialogFooter>
              <Button variant="primary" onClick={() => onOpenChange(false)}>
                Done
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogBody className="space-y-4">
              <Field label="Label" hint="Just for you — shown in the list below, not on the page.">
                {(props) => (
                  <Input {...props} value={label} onChange={(e) => setLabel(e.target.value)} />
                )}
              </Field>
              <Field label="Password" hint="Leave blank for an open link that needs no password.">
                {(props) => (
                  <Input
                    {...props}
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                  />
                )}
              </Field>
              <Field
                label="Expires"
                hint="Leave blank for a link that only ever expires when you revoke it."
              >
                {(props) => (
                  <Input
                    {...props}
                    type="datetime-local"
                    value={expiresAt}
                    onChange={(e) => setExpiresAt(e.target.value)}
                  />
                )}
              </Field>
            </DialogBody>
            <DialogFooter>
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={() => create.mutate()} disabled={create.isPending}>
                {create.isPending ? 'Creating…' : 'Create link'}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
